import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import styles from './D3UsageChart.module.css';

const D3UsageChart = ({ data, timePeriod, theme }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Debug logging
  console.log('D3UsageChart - Raw data:', data);
  console.log('D3UsageChart - Data length:', data?.length);
  console.log('D3UsageChart - Sample item:', data?.[0]);

  // Handle responsive resizing
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.max(400, width - 40), // Min width 400px, subtract padding
          height: 350
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Main D3 chart rendering
  useEffect(() => {
    if (!data || !Array.isArray(data) || data.length === 0 || dimensions.width === 0) {
      return;
    }

    // Helper function to parse UTC timestamps from backend
    const parseUTCTimestamp = (timestamp) => {
      if (!timestamp) return new Date();
      
      // Ensure timestamp is treated as UTC if it doesn't have timezone info
      let dateString = timestamp;
      if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+')) {
        // Add 'Z' to indicate UTC if no timezone info present
        dateString = timestamp + 'Z';
      }
      
      return new Date(dateString);
    };

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous render

    // Chart dimensions and margins
    const margin = { top: 40, right: 30, bottom: 80, left: 70 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    // Create main group
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Process data - handle both camelCase and snake_case
    const processedData = data.map(d => ({
      timestamp: d.timestamp,
      podCost: d.podCost || d.pod_cost || 0,
      llmCost: d.llmCost || d.llm_cost || 0,
      totalCost: (d.podCost || d.pod_cost || 0) + (d.llmCost || d.llm_cost || 0)
    }));
    
    const maxValue = Math.max(...processedData.map(d => d.totalCost));
    
    console.log('D3 Processing - processedData:', processedData);
    console.log('D3 Processing - maxValue:', maxValue);

    // Create scales
    const xScale = d3.scaleBand()
      .domain(processedData.map((_, i) => i))
      .range([0, width])
      .padding(0.2);

    const yScale = d3.scaleLinear()
      .domain([0, maxValue === 0 ? 1 : maxValue * 1.1])
      .range([height, 0]);

    // Create axes
    const xAxis = d3.axisBottom(xScale)
      .tickFormat((d, i) => {
        const item = processedData[i];
        if (!item) return '';
        
        const date = parseUTCTimestamp(item.timestamp);
        // Use browser's locale and timezone automatically
        if (timePeriod === '24h') {
          return date.toLocaleTimeString(undefined, { hour: 'numeric', hour12: true });
        } else if (['3d', '7d', '30d'].includes(timePeriod)) {
          return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        } else {
          return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
        }
      });

    const yAxis = d3.axisLeft(yScale)
      .ticks(6)
      .tickFormat(d => `$${d.toFixed(2)}`);

    // Add grid lines
    g.append('g')
      .attr('class', styles.grid)
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .tickSize(-height)
        .tickFormat('')
      );

    g.append('g')
      .attr('class', styles.grid)
      .call(d3.axisLeft(yScale)
        .ticks(6)
        .tickSize(-width)
        .tickFormat('')
      );

    // Add axes
    g.append('g')
      .attr('class', styles.xAxis)
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)');

    g.append('g')
      .attr('class', styles.yAxis)
      .call(yAxis);

    // Add axis labels
    g.append('text')
      .attr('class', styles.axisLabel)
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left)
      .attr('x', 0 - (height / 2))
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .text('Cost ($)');

    g.append('text')
      .attr('class', styles.axisLabel)
      .attr('transform', `translate(${width / 2}, ${height + margin.bottom - 10})`)
      .style('text-anchor', 'middle')
      .text(() => {
        if (timePeriod === '24h') return 'Time (24h)';
        if (['3d', '7d', '30d'].includes(timePeriod)) return 'Date';
        return 'Month';
      });

    // Create tooltip
    const tooltip = d3.select('body').selectAll('.d3-tooltip')
      .data([0])
      .join('div')
      .attr('class', 'd3-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('padding', '8px 12px')
      .style('border-radius', '6px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('z-index', '1000');

    // Create stacked bar groups
    const barGroups = g.selectAll('.bar-group')
      .data(processedData)
      .join('g')
      .attr('class', 'bar-group')
      .attr('transform', (_, i) => `translate(${xScale(i)}, 0)`);

    // Add Servers bars (green) - bottom layer
    const podBars = barGroups.append('rect')
      .attr('class', styles.podBar)
      .attr('width', xScale.bandwidth())
      .attr('y', height) // Start from bottom for animation
      .attr('height', 0)
      .style('fill', '#10b981') // Green color for Servers
      .style('stroke', 'rgba(255, 255, 255, 0.1)')
      .style('stroke-width', '1px');

    // Add LLM API Calls bars (blue) - top layer (stacked on pods)
    const llmBars = barGroups.append('rect')
      .attr('class', styles.llmBar)
      .attr('width', xScale.bandwidth())
      .attr('y', height) // Start from bottom for animation
      .attr('height', 0)
      .style('fill', '#3b82f6') // Blue color for LLM API Calls
      .style('stroke', 'rgba(255, 255, 255, 0.1)')
      .style('stroke-width', '1px');

    // Animate pod bars (bottom layer)
    podBars.transition()
      .duration(800)
      .ease(d3.easeBackOut.overshoot(1.1))
      .attr('y', d => yScale(d.podCost))
      .attr('height', d => height - yScale(d.podCost));

    // Animate LLM bars (top layer, stacked)
    llmBars.transition()
      .duration(800)
      .ease(d3.easeBackOut.overshoot(1.1))
      .attr('y', d => yScale(d.totalCost))
      .attr('height', d => yScale(d.podCost) - yScale(d.totalCost));

    // Add hover effects to the entire bar group
    barGroups.on('mouseover', function(event, d) {
        // Highlight both bars in the stack
        d3.select(this).selectAll('rect')
          .transition()
          .duration(200)
          .style('opacity', 0.8);

        const date = parseUTCTimestamp(d.timestamp);
        let timeLabel;
        // Use browser's locale and timezone for tooltip
        if (timePeriod === '24h') {
          timeLabel = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
        } else if (['3d', '7d', '30d'].includes(timePeriod)) {
          timeLabel = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        } else {
          timeLabel = date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
        }

        tooltip
          .style('visibility', 'visible')
          .html(`
            <div><strong>${timeLabel}</strong></div>
            <div style="margin-top: 4px;">Total: <strong>$${d.totalCost.toFixed(2)}</strong></div>
            <div style="display: flex; align-items: center; margin-top: 4px;">
              <div style="width: 10px; height: 10px; background: #10b981; border-radius: 2px; margin-right: 6px;"></div>
              Servers: $${d.podCost.toFixed(2)}
            </div>
            <div style="display: flex; align-items: center; margin-top: 2px;">
              <div style="width: 10px; height: 10px; background: #3b82f6; border-radius: 2px; margin-right: 6px;"></div>
              LLM API Calls: $${d.llmCost.toFixed(2)}
            </div>
          `);
      })
      .on('mousemove', function(event) {
        tooltip
          .style('top', (event.pageY - 10) + 'px')
          .style('left', (event.pageX + 10) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this).selectAll('rect')
          .transition()
          .duration(200)
          .style('opacity', 1);

        tooltip.style('visibility', 'hidden');
      });

    // Add total value labels on top of stacked bars (for non-zero values)
    const labels = g.selectAll('.bar-label')
      .data(processedData.filter(d => d.totalCost > 0))
      .join('text')
      .attr('class', styles.barLabel)
      .attr('x', (_, i) => {
        const originalIndex = processedData.findIndex(d => d === processedData.filter(item => item.totalCost > 0)[i]);
        return xScale(originalIndex) + xScale.bandwidth() / 2;
      })
      .attr('y', d => yScale(d.totalCost) - 5)
      .attr('text-anchor', 'start') // Changed from 'middle' to 'start' for angled text
      .attr('transform', (_, i) => {
        const originalIndex = processedData.findIndex(d => d === processedData.filter(item => item.totalCost > 0)[i]);
        const x = xScale(originalIndex) + xScale.bandwidth() / 2;
        const y = yScale(processedData.filter(item => item.totalCost > 0)[i].totalCost) - 5;
        return `rotate(-45, ${x}, ${y})`; // Rotate 45 degrees counter-clockwise around the label position
      })
      .style('font-size', '11px')
      .style('font-weight', '600')
      .style('fill', 'currentColor')
      .style('opacity', 0)
      .text(d => `$${d.totalCost.toFixed(2)}`);

    // Animate labels
    labels.transition()
      .delay(400)
      .duration(400)
      .style('opacity', 0.8);

  }, [data, timePeriod, dimensions, theme]);

  // Show loading or no data state
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div ref={containerRef} className={styles.chartContainer}>
        <div className={styles.noData}>
          <div className={styles.noDataIcon}>📊</div>
          <p>No usage data available for this period</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={styles.chartContainer}>
      <div className={styles.chartHeader}>
        <h3>Usage Cost Over Time</h3>
      </div>
      
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        className={styles.svg}
      />
      
      <div className={styles.chartLegend}>
        <div className={styles.legendItem}>
          <div className={`${styles.legendColor}`} style={{backgroundColor: '#10b981'}}></div>
          <span>Servers</span>
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendColor}`} style={{backgroundColor: '#3b82f6'}}></div>
          <span>LLM API Calls</span>
        </div>
      </div>
    </div>
  );
};

export default D3UsageChart;
